import React from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Paper,
  Divider,
  Button,
  Card,
  CardContent,
  //Avatar
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';

// Import available images
import founderImage from '../assets/images/founder.jpg';
import aboutHero from '../assets/images/about-hero.jpg';

// Icons for values (until you have value icons)
import FavoriteIcon from '@mui/icons-material/Favorite';
import HighQualityIcon from '@mui/icons-material/HighQuality';
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
//import PersonIcon from '@mui/icons-material/Person';

const About = () => {
  const navigate = useNavigate();

  return (
    <>
      <Helmet>
        <title>About Us | Memory Link</title>
        <meta name="description" content="Learn about Memory Link. We provide premium QR codes for gravestones and memorials throughout the UK, preserving memories for generations to come." />
        <meta name="keywords" content="about digital memorials, memorial QR code company, gravestone QR code UK, headstone QR code service, memorial QR code history, digital memorial company" />
        <link rel="canonical" href="https://digitalmemorials.co.uk/about" />
      </Helmet>

      {/* Hero Section - Now with actual image */}
      <Box
        sx={{
          background: `linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)), url(${aboutHero})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          color: 'white',
          py: 10,
          textAlign: 'center'
        }}
      >
        <Container maxWidth="md">
          <Typography 
            variant="h2" 
            component="h1" 
            gutterBottom
            sx={{ 
              fontWeight: 700,
              mb: 2
            }}
          >
            About Memory Link
          </Typography>
          <Typography 
            variant="h5"
            sx={{ 
              maxWidth: '800px',
              mx: 'auto',
              mb: 4,
              fontWeight: 300
            }}
          >
            Preserving memories and connecting generations through innovative memorial technology
          </Typography>
        </Container>
      </Box>

      {/* Our Story Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 700 }}>
              Our Story
            </Typography>
            <Typography variant="body1" paragraph sx={{ mb: 3 }}>
              Memory Link was founded with a simple yet profound mission: to bridge the physical and digital worlds to help preserve the memories of our loved ones for generations to come.
            </Typography>
            <Typography variant="body1" paragraph sx={{ mb: 3 }}>
              Our journey began when our founder experienced the loss of a close family member and realized that traditional memorials, while beautiful and important, could tell only a small part of a person's story. The rich tapestry of photos, videos, stories, and memories that truly captured their loved one's essence had no place on a traditional headstone.
            </Typography>
            <Typography variant="body1" paragraph>
              This realization led to the creation of Memory Link. We've combined durable, weatherproof QR code technology with thoughtfully designed digital memorial pages to create a bridge between traditional physical memorials and the unlimited possibilities of digital remembrance.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper 
              elevation={0} 
              sx={{ 
                p: 4, 
                borderRadius: 4, 
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'grey.200',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                textAlign: 'center'
              }}
            >
              <Box
                component="img"
                src={founderImage}
                alt="Founder of Memory Link"
                sx={{
                  width: 180,
                  height: 250,
                  objectFit: 'cover',
                  borderRadius: '60%',
                  mx: 'auto',
                  mb: 3,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }}
              />
              <Typography variant="h6" align="center" gutterBottom sx={{ fontWeight: 600 }}>
                Our Founder
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center">
                "We believe that every life tells a unique story that deserves to be preserved and shared across generations."
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Our Mission & Values */}
      <Box sx={{ bgcolor: 'grey.100', py: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 700 }}>
              Our Mission & Values
            </Typography>
            <Typography variant="body1" sx={{ maxWidth: '800px', mx: 'auto' }}>
              At Memory Link, we're guided by core principles that shape everything we do.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', boxShadow: 'none', border: '1px solid', borderColor: 'grey.200' }}>
                <CardContent sx={{ textAlign: 'center', p: 4 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      mb: 2
                    }}
                  >
                    <FavoriteIcon sx={{ fontSize: 60, color: 'grey.600' }} />
                  </Box>
                  <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 600 }}>
                    Respect
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    We approach every memorial with the utmost respect and dignity. We understand the sacredness of remembering loved ones and treat each memorial with the care it deserves.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', boxShadow: 'none', border: '1px solid', borderColor: 'grey.200' }}>
                <CardContent sx={{ textAlign: 'center', p: 4 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      mb: 2
                    }}
                  >
                    <HighQualityIcon sx={{ fontSize: 60, color: 'grey.600' }} />
                  </Box>
                  <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 600 }}>
                    Quality
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    We use only premium materials designed to withstand the elements for generations. Our QR codes are crafted from high-grade stainless steel with specialized UV and weather-resistant coatings.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Card sx={{ height: '100%', boxShadow: 'none', border: '1px solid', borderColor: 'grey.200' }}>
                <CardContent sx={{ textAlign: 'center', p: 4 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      mb: 2
                    }}
                  >
                    <AllInclusiveIcon sx={{ fontSize: 60, color: 'grey.600' }} />
                  </Box>
                  <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 600 }}>
                    Permanence
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    We are committed to the long-term preservation of your memories. Our digital memorial platform is designed with future-proofing in mind, and we maintain secure backups to ensure your memorial lasts for generations.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* How It Works */}
      {/* How It Works */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 700, textAlign: 'center', mb: 6 }}>
          How Our Memorial QR Codes Work
        </Typography>

        <Grid container spacing={4}>
          <Grid item xs={12} md={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography 
                variant="h1" 
                sx={{ 
                  color: 'grey.300', 
                  fontWeight: 900, 
                  fontSize: '4rem',
                  mb: 2
                }}
              >
                01
              </Typography>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Purchase Your QR Code
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Purchase our premium memorial QR code, currently available in one high-quality option designed to last for generations.
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography 
                variant="h1" 
                sx={{ 
                  color: 'grey.300', 
                  fontWeight: 900, 
                  fontSize: '4rem',
                  mb: 2
                }}
              >
                02
              </Typography>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Receive Your QR Code
              </Typography>
              <Typography variant="body2" color="text.secondary">
                We'll deliver your premium, weatherproof QR code with detailed instructions for installation and the next steps to create your memorial.
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography 
                variant="h1" 
                sx={{ 
                  color: 'grey.300', 
                  fontWeight: 900, 
                  fontSize: '4rem',
                  mb: 2
                }}
              >
                03
              </Typography>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Register & Create Memorial
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Follow the simple steps in our instruction letter to register and create a beautiful digital memorial with photos, videos, and stories.
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={3}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography 
                variant="h1" 
                sx={{ 
                  color: 'grey.300', 
                  fontWeight: 900, 
                  fontSize: '4rem',
                  mb: 2
                }}
              >
                04
              </Typography>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Connect Generations
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Visitors can scan the QR code with any smartphone to access the memorial, connecting the physical monument with unlimited digital memories.
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Box sx={{ textAlign: 'center', mt: 8 }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={() => navigate('/#shop-section')}
            sx={{ 
              px: 4, 
              py: 1.5,
              borderRadius: 30,
              fontWeight: 600
            }}
          >
            Explore Our Products
          </Button>
        </Box>
      </Container>

      {/* Material & Technology */}
      <Box sx={{ bgcolor: 'grey.900', color: 'white', py: 8 }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 700 }}>
                Our Materials & Technology
              </Typography>
              <Typography variant="body1" paragraph sx={{ color: 'grey.300', mb: 3 }}>
                We understand that memorials are meant to last for generations, which is why we use only the highest quality materials for our QR codes.
              </Typography>
              <Typography variant="body1" paragraph sx={{ color: 'grey.300', mb: 1 }}>
                <strong>Premium Stainless Steel:</strong> Our QR codes are crafted from 316 marine-grade stainless steel, known for its exceptional durability and resistance to corrosion even in harsh outdoor environments.
              </Typography>
              <Typography variant="body1" paragraph sx={{ color: 'grey.300', mb: 1 }}>
                <strong>UV & Weather Protection:</strong> We apply specialized UV-resistant coatings to ensure the QR code remains scannable for decades, even with constant exposure to sunlight, rain, and changing temperatures.
              </Typography>
              <Typography variant="body1" paragraph sx={{ color: 'grey.300' }}>
                <strong>Secure Digital Platform:</strong> Our digital memorials are hosted on secure, redundant servers with regular backups to ensure they remain accessible for future generations.
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper 
                sx={{ 
                  p: 4, 
                  bgcolor: 'grey.800', 
                  color: 'white',
                  borderRadius: 4,
                  border: '1px solid',
                  borderColor: 'grey.700'
                }}
              >
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: 'grey.100' }}>
                  Our Durability Promise
                </Typography>
                <Typography variant="body2" paragraph sx={{ color: 'grey.300', mb: 3 }}>
                  We stand behind the durability of our memorial QR codes with our promise:
                </Typography>
                <ul style={{ color: 'grey.300', paddingLeft: '20px' }}>
                  <li>
                    <Typography variant="body2" sx={{ mb: 1, color: 'grey.300' }}>
                      Our QR codes are designed to last for at least 50 years in outdoor conditions
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2" sx={{ mb: 1, color: 'grey.300' }}>
                      If a QR code ever becomes unscannable due to material failure, we'll replace it free of charge
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2" sx={{ mb: 1, color: 'grey.300' }}>
                      Your digital memorial is hosted for a lifetime, with no ongoing fees or subscriptions
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2" sx={{ color: 'grey.300' }}>
                      We maintain secure backups of all memorial content to ensure it's never lost
                    </Typography>
                  </li>
                </ul>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Our Team - Simplified */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 700, textAlign: 'center', mb: 2 }}>
          Our Team
        </Typography>
        <Typography variant="body1" sx={{ textAlign: 'center', maxWidth: '800px', mx: 'auto', mb: 6 }}>
          Meet the dedicated people behind Digital Memorials  who are passionate about preserving memories and honoring loved ones.
        </Typography>

        <Grid container spacing={4} justifyContent="center">
          <Grid item xs={12} sm={6} md={4}>
            <Card sx={{ height: '100%', boxShadow: 'none', border: '1px solid', borderColor: 'grey.200' }}>
              <CardContent sx={{ textAlign: 'center', p: 4 }}>
                <Box
                  component="img"
                  src={founderImage}
                  alt="Founder"
                  sx={{
                    width: 150,
                    height: 150,
                    objectFit: 'cover',
                    borderRadius: '50%',
                    mx: 'auto',
                    mb: 3,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                  }}
                />
                <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 600 }}>
                  Javier Ruiz
                </Typography>
                <Typography variant="body2" color="primary" gutterBottom>
                  Founder & Director
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  Javier founded Memory Link with a vision to revolutionize how we preserve and share memories of our loved ones.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Our Commitment / Call To Action */}
      <Box sx={{ bgcolor: 'primary.main', color: 'white', py: 8, textAlign: 'center' }}>
        <Container maxWidth="md">
          <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 700 }}>
            Our Commitment to You
          </Typography>
          <Typography variant="body1" paragraph sx={{ maxWidth: '800px', mx: 'auto', mb: 4 }}>
            At Memory Link, we understand the profound responsibility of preserving memories of your loved ones. We promise to handle every memorial with care, respect, and dignity, and to provide a service that honors their legacy for generations to come.
          </Typography>
          <Button
            variant="contained"
            color="secondary"
            size="large"
            onClick={() => navigate('/')}
            sx={{ 
              px: 4, 
              py: 1.5,
              borderRadius: 30,
              fontWeight: 600,
              bgcolor: 'white',
              color: 'primary.main',
              '&:hover': {
                bgcolor: 'grey.100'
              }
            }}
          >
            Back to Home
          </Button>
        </Container>
      </Box>
    </>
  );
};

export default About;